import { isNetworkFailure } from "@/lib/api/error-message";
import {
  completeUpload,
  getUploadSession,
  isGalleryRateLimited,
  PartUploadError,
  presignUploadParts,
  uploadPresignedPart,
} from "./api";
import type { GalleryAssetVersion, GalleryCompletedPart, GalleryUploadSession } from "./types";

export type UploadManagerState =
  | "queued"
  | "uploading"
  | "completing"
  | "paused"
  | "processing"
  | "ready"
  | "failed";

export type UploadProgress = {
  uploadId: string;
  fileIndex?: number;
  uploadedBytes: number;
  totalBytes: number;
  partNumber?: number;
  state: Extract<UploadManagerState, "uploading" | "completing" | "processing" | "ready">;
};

export type UploadCheckpoint = {
  uploadId: string;
  fingerprint: string;
  parts: GalleryCompletedPart[];
  updatedAt: number;
};

export interface CheckpointStore {
  load(uploadId: string): Promise<UploadCheckpoint | undefined>;
  list(): Promise<UploadCheckpoint[]>;
  save(checkpoint: UploadCheckpoint): Promise<void>;
  remove(uploadId: string): Promise<void>;
}

export class MemoryCheckpointStore implements CheckpointStore {
  private readonly values = new Map<string, UploadCheckpoint>();
  readonly history: Array<UploadCheckpoint | null> = [];

  async load(uploadId: string) {
    const value = this.values.get(uploadId);
    return value ? structuredClone(value) : undefined;
  }

  async list() {
    return [...this.values.values()].map((value) => structuredClone(value));
  }

  async save(checkpoint: UploadCheckpoint) {
    const clone = structuredClone(checkpoint);
    this.values.set(checkpoint.uploadId, clone);
    this.history.push(clone);
  }

  async remove(uploadId: string) {
    this.values.delete(uploadId);
    this.history.push(null);
  }
}

class IndexedDBCheckpointStore implements CheckpointStore {
  private readonly databaseName = "gnpf-gallery-uploads";
  private readonly objectStore = "checkpoints";

  private open(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.databaseName, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(this.objectStore)) {
          request.result.createObjectStore(this.objectStore, { keyPath: "uploadId" });
        }
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("Không mở được dữ liệu tải tiếp"));
    });
  }

  async load(uploadId: string): Promise<UploadCheckpoint | undefined> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.objectStore, "readonly");
      const request = transaction.objectStore(this.objectStore).get(uploadId);
      request.onsuccess = () => resolve(request.result as UploadCheckpoint | undefined);
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  }

  async list(): Promise<UploadCheckpoint[]> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.objectStore, "readonly");
      const request = transaction.objectStore(this.objectStore).getAll();
      request.onsuccess = () => resolve(request.result as UploadCheckpoint[]);
      request.onerror = () => reject(request.error);
      transaction.oncomplete = () => db.close();
    });
  }

  async save(checkpoint: UploadCheckpoint): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.objectStore, "readwrite");
      transaction.objectStore(this.objectStore).put(checkpoint);
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  }

  async remove(uploadId: string): Promise<void> {
    const db = await this.open();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.objectStore, "readwrite");
      transaction.objectStore(this.objectStore).delete(uploadId);
      transaction.oncomplete = () => {
        db.close();
        resolve();
      };
      transaction.onerror = () => {
        db.close();
        reject(transaction.error);
      };
    });
  }
}

let fallbackStore: MemoryCheckpointStore | undefined;

export function defaultCheckpointStore(): CheckpointStore {
  if (typeof indexedDB !== "undefined") return new IndexedDBCheckpointStore();
  fallbackStore ??= new MemoryCheckpointStore();
  return fallbackStore;
}

type UploadOptions = {
  store?: CheckpointStore;
  /** Số lần gửi lại một phần ảnh hỏng (mặc định theo NETWORK_RETRY_DELAYS_MS). */
  maxRetries?: number;
  /** Bài kiểm đặt lịch chờ riêng: lần thứ i chờ `retryDelayMs * 2^i`. */
  retryDelayMs?: number;
  /** Đồng hồ đo tuổi link gửi phần (bài kiểm thay được). */
  now?: () => number;
  onProgress?: (progress: UploadProgress) => void;
  /** Chờ tới lượt trước MỖI lần gọi máy chủ (xin link tải phần, hoàn tất). */
  beforeApiCall?: () => Promise<void>;
  /** Số tệp tải cùng lúc trong `uploadFiles` (mặc định UPLOAD_CONCURRENCY). */
  concurrency?: number;
  /** Số phần của MỘT tệp tải cùng lúc (mặc định 1 — từng phần một). */
  partConcurrency?: number;
  /** Bị chặn vì gọi quá nhanh: báo hộp tải để hiện "đang chờ…" thay vì báo lỗi. */
  onRateLimited?: (waitMs: number) => void;
  /**
   * Tải xong một tệp: cả chặng, và riêng lúc gửi thành công các phần còn thiếu
   * (gửi nhiều phần cùng lúc thì `sendMs` cộng chồng lên nhau).
   */
  onTimed?: (timing: { totalMs: number; sendMs: number }) => void;
};

/**
 * Bị 429 thì chờ rồi gọi lại đúng lượt đó, tối đa chừng này lần cho mỗi lượt.
 * Trần đếm theo phút, nên chờ hết phút là qua; tới đây mà vẫn bị chặn là có gì
 * khác đang ăn hết trần của người này — dừng và báo lý do.
 */
export const RATE_LIMIT_RETRIES = 5;
/** Máy chủ không gửi Retry-After thì chờ chừng này rồi thử lại. */
const RATE_LIMIT_FALLBACK_MS = 15_000;

/**
 * Lịch chờ trước mỗi lần gửi lại khi mạng hỏng — cộng ~1 phút (CEO chốt 1a,
 * 24/10/2026). Trước đó một phần ảnh chỉ thử lại 3 lần trong ~2,8 giây, còn xin
 * link / hoàn tất thì không thử lại: mạng 4G/5G chập chờn lâu hơn thế là cả lô
 * dừng, phải bấm "Thử lại" (lượt kiểm 24/10/2026, L4).
 */
export const NETWORK_RETRY_DELAYS_MS: readonly number[] = [1_000, 2_000, 4_000, 8_000, 15_000, 30_000];

/**
 * Link gửi phần sống 15 phút (gallerySignedPartTTL ở máy chủ) và được ký một lần
 * cho cả tấm. Phần sắp gửi mà link đã cũ hơn chừng này thì xin link mới trước:
 * mạng ~10 KB/giây gửi một phần 8 MB mất ~13 phút, tới phần thứ ba là link đã
 * chết (lượt kiểm 24/10/2026, N1).
 */
export const PART_URL_REFRESH_MS = 10 * 60_000;

/** Không tới được máy chủ: máy khách đổi TypeError của fetch thành câu tiếng Việt, lỗi gốc nằm ở `cause`. */
function isNetworkLoss(error: unknown): boolean {
  return isNetworkFailure(error) || (error instanceof Error && isNetworkFailure(error.cause));
}

/**
 * Gọi máy chủ (xin link, giữ chỗ, hoàn tất — cả ba gọi lại không sinh trùng).
 * Bị chặn vì gọi quá nhanh (429) thì chờ theo Retry-After rồi gọi lại, thay vì
 * dừng cả lô (trước 18/10/2026 một tấm bị 429 là cả lô dừng ở câu "Thao tác quá
 * nhanh" — đo trên prod). Không tới được máy chủ thì chờ có mạng rồi gọi lại
 * theo NETWORK_RETRY_DELAYS_MS. Lỗi khác ném ra ngay.
 */
export async function callWithRateLimitRetry<T>(
  call: () => Promise<T>,
  signal: AbortSignal,
  onRateLimited?: (waitMs: number) => void,
): Promise<T> {
  let rateLimited = 0;
  let networkLost = 0;
  for (;;) {
    try {
      return await call();
    } catch (cause) {
      if (signal.aborted) throw cause;
      if (isGalleryRateLimited(cause) && rateLimited < RATE_LIMIT_RETRIES) {
        rateLimited += 1;
        const waitMs = cause.retryAfterSeconds ? cause.retryAfterSeconds * 1000 + 250 : RATE_LIMIT_FALLBACK_MS;
        onRateLimited?.(waitMs);
        await wait(waitMs, signal);
        continue;
      }
      if (isNetworkLoss(cause) && networkLost < NETWORK_RETRY_DELAYS_MS.length) {
        const delay = NETWORK_RETRY_DELAYS_MS[networkLost] ?? 0;
        networkLost += 1;
        await waitOnline(signal);
        await wait(delay, signal);
        continue;
      }
      throw cause;
    }
  }
}

/** Tải 4 tệp cùng lúc (CEO chốt 16/10/2026; trước đó 2 từ rà soát album 13/10/2026,
 *  I4) — mạng chập chờn / trễ cao thì mỗi đường truyền đi chậm, thêm đường là thêm
 *  tốc độ, ảnh giữ nguyên không nén. Trần 429 không còn là lý do giữ ít: nhịp gọi
 *  máy chủ đã có `createCallLimiter` giữ, bất kể bao nhiêu tệp chạy song song. */
export const UPLOAD_CONCURRENCY = 4;

/**
 * Trần nhịp gọi máy chủ của hộp tải: máy chủ chặn 300 lượt/phút cho mỗi người
 * (RATE_LIMIT_PER_USER_PER_MIN) và đếm CHUNG với mọi màn khác đang mở. Mỗi tệp tốn
 * hai lượt (xin link + hoàn tất); ảnh nhỏ tải nhanh thì ngay cả tải từng tệp một
 * cũng chạm 300 và cả lô đỏ 429. Giữ 200 lượt/phút cho hộp tải, chừa phần còn lại.
 */
export const UPLOAD_API_CALLS_PER_MINUTE = 200;

/** Cửa xếp hàng theo cửa sổ trượt 60 giây: quá `perMinute` lượt thì đợi lượt cũ nhất hết hạn. */
export function createCallLimiter(
  perMinute: number,
  now: () => number = () => Date.now(),
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
): () => Promise<void> {
  const calls: number[] = [];
  let queue = Promise.resolve();
  return () => {
    const turn = queue.then(async () => {
      for (;;) {
        const t = now();
        while (calls.length > 0 && t - (calls[0] ?? 0) >= 60_000) calls.shift();
        if (calls.length < perMinute) {
          calls.push(t);
          return;
        }
        await sleep(60_000 - (t - (calls[0] ?? t)) + 5);
      }
    });
    queue = turn.catch(() => undefined);
    return turn;
  };
}

function fingerprint(file: File): string {
  return `${file.size}:${file.lastModified}:${file.type}`;
}

export function fileFingerprint(file: File): string {
  return fingerprint(file);
}

function abortError(): DOMException {
  return new DOMException("Đã tạm dừng tải ảnh", "AbortError");
}

function wait(milliseconds: number, signal: AbortSignal): Promise<void> {
  if (milliseconds <= 0) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(resolve, milliseconds);
    signal.addEventListener(
      "abort",
      () => {
        clearTimeout(timeout);
        reject(abortError());
      },
      { once: true },
    );
  });
}

/**
 * Máy báo đang mất mạng (`navigator.onLine` = false) thì đợi có mạng lại rồi
 * mới gửi lại — gửi lúc mất mạng chỉ đốt lượt thử. Có mạng mà vẫn không tới
 * được máy chủ thì lịch chờ NETWORK_RETRY_DELAYS_MS lo.
 */
export function waitOnline(signal?: AbortSignal): Promise<void> {
  if (typeof navigator === "undefined" || navigator.onLine !== false) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearInterval(poll);
      window.removeEventListener("online", check);
      signal?.removeEventListener("abort", abort);
    };
    function check() {
      if (navigator.onLine === false) return;
      cleanup();
      resolve();
    }
    function abort() {
      cleanup();
      reject(abortError());
    }
    // Hỏi lại định kỳ phòng khi lỡ sự kiện "online".
    const poll = window.setInterval(check, 5_000);
    window.addEventListener("online", check);
    signal?.addEventListener("abort", abort, { once: true });
    if (signal?.aborted) abort();
  });
}

/**
 * Lỗi gửi phần đáng gửi lại: không tới được kho hay kho treo (0), link hết hạn
 * (403 — lần sau xin link mới), kho bận (408, 429, 5xx). Lỗi khác (404 lượt tải
 * không còn, 400…) gửi lại cũng vậy — ném ra ngay.
 */
function isRetryablePartError(error: unknown): boolean {
  if (!(error instanceof PartUploadError)) return true;
  const { status } = error;
  return status === 0 || status === 403 || status === 408 || status === 429 || status >= 500;
}

function partRetryDelays(options: UploadOptions): number[] {
  const count = options.maxRetries ?? NETWORK_RETRY_DELAYS_MS.length;
  return Array.from({ length: count }, (_, attempt) =>
    options.retryDelayMs !== undefined
      ? options.retryDelayMs * 2 ** attempt
      : (NETWORK_RETRY_DELAYS_MS[Math.min(attempt, NETWORK_RETRY_DELAYS_MS.length - 1)] ?? 0),
  );
}

async function putWithRetry(
  urlFor: (fresh: boolean) => Promise<string>,
  body: Blob,
  signal: AbortSignal,
  delays: readonly number[],
  onProgress?: (uploadedBytes: number) => void,
  now: () => number = Date.now,
  onSent?: (sendMs: number) => void,
): Promise<string> {
  let fresh = false;
  for (let attempt = 0; ; attempt += 1) {
    if (signal.aborted) throw abortError();
    const url = await urlFor(fresh);
    const sendAt = now();
    try {
      const eTag = await uploadPresignedPart(url, body, signal, onProgress);
      onSent?.(now() - sendAt);
      return eTag;
    } catch (cause) {
      if (signal.aborted || !isRetryablePartError(cause) || attempt >= delays.length) throw cause;
      onProgress?.(0);
      fresh = cause instanceof PartUploadError && cause.status === 403;
      await waitOnline(signal);
      await wait(delays[attempt] ?? 0, signal);
    }
  }
}

/**
 * Link gửi từng phần của một tấm: ký mọi phần chưa gửi một lượt; phần sắp gửi
 * mà chưa có link, link đã cũ (PART_URL_REFRESH_MS) hay vừa bị kho chê hết hạn
 * (403, `fresh`) thì ký lại các phần còn lại.
 */
function partSigner(
  sessionId: string,
  pending: () => number[],
  signal: AbortSignal,
  options: UploadOptions,
): (partNumber: number, fresh: boolean) => Promise<string> {
  const now = options.now ?? Date.now;
  let signedByPart = new Map<number, string>();
  let signedAt = 0;
  // Parts sent side by side (partConcurrency) share one signing call.
  let signing: Promise<void> | null = null;
  const sign = () =>
    (signing ??= (async () => {
      const partNumbers = pending();
      const signedParts = await callWithRateLimitRetry(
        async () => {
          await options.beforeApiCall?.();
          return presignUploadParts(sessionId, partNumbers);
        },
        signal,
        options.onRateLimited,
      );
      signedByPart = new Map(signedParts.map((part) => [part.partNumber, part.url]));
      signedAt = now();
    })().finally(() => {
      signing = null;
    }));
  return async (partNumber, fresh) => {
    if (fresh || !signedByPart.has(partNumber) || now() - signedAt >= PART_URL_REFRESH_MS) await sign();
    const url = signedByPart.get(partNumber);
    if (!url) throw new Error(`Thiếu liên kết tải phần ${partNumber}`);
    return url;
  };
}

function uploadedBytesForParts(
  parts: Iterable<number>,
  fileSize: number,
  partSizeBytes: number,
): number {
  let uploadedBytes = 0;
  for (const partNumber of parts) {
    const start = (partNumber - 1) * partSizeBytes;
    uploadedBytes += Math.max(0, Math.min(start + partSizeBytes, fileSize) - start);
  }
  return Math.min(uploadedBytes, fileSize);
}

export async function uploadFile(
  session: GalleryUploadSession,
  file: File,
  signal: AbortSignal,
  options: UploadOptions = {},
): Promise<GalleryAssetVersion> {
  if (session.status === "completed") {
    throw new Error("Lượt tải này đã hoàn tất");
  }
  if (session.status !== "initiated") {
    throw new Error("Lượt tải này không còn hoạt động");
  }
  if (file.size !== session.sizeBytes || file.type !== session.mime) {
    throw new Error("Tệp được chọn không khớp lượt tải đang tiếp tục");
  }
  if (signal.aborted) throw abortError();

  const now = options.now ?? Date.now;
  const startedAt = now();
  let sendMs = 0;
  const store = options.store ?? defaultCheckpointStore();
  const fileFingerprint = fingerprint(file);
  const saved = await store.load(session.id);
  const completed = new Map<number, string>();
  if (saved?.fingerprint === fileFingerprint) {
    for (const part of saved.parts) completed.set(part.partNumber, part.eTag);
  }
  const emitProgress = (
    state: UploadProgress["state"],
    uploadedBytes: number,
    partNumber?: number,
  ) => {
    options.onProgress?.({
      uploadId: session.id,
      uploadedBytes: Math.min(uploadedBytes, file.size),
      totalBytes: file.size,
      ...(partNumber ? { partNumber } : {}),
      state,
    });
  };

  const totalParts = Math.ceil(file.size / session.partSizeBytes);
  let persistedBytes = uploadedBytesForParts(completed.keys(), file.size, session.partSizeBytes);
  if (persistedBytes > 0) emitProgress("uploading", persistedBytes);
  const missing = Array.from({ length: totalParts }, (_, index) => index + 1).filter(
    (partNumber) => !completed.has(partNumber),
  );
  if (missing.length > 0) {
    // URLs are intentionally short-lived and never enter the checkpoint store.
    const urlFor = partSigner(
      session.id,
      () => missing.filter((number) => !completed.has(number)),
      signal,
      options,
    );
    const delays = partRetryDelays(options);
    // Bytes sent so far of each part still uploading.
    const inFlight = new Map<number, number>();
    const inFlightBytes = () => [...inFlight.values()].reduce((sum, bytes) => sum + bytes, 0);
    const uploadPart = async (partNumber: number) => {
      const start = (partNumber - 1) * session.partSizeBytes;
      const end = Math.min(start + session.partSizeBytes, file.size);
      inFlight.set(partNumber, 0);
      const eTag = await putWithRetry(
        (fresh) => urlFor(partNumber, fresh),
        file.slice(start, end),
        signal,
        delays,
        (partUploadedBytes) => {
          inFlight.set(partNumber, partUploadedBytes);
          emitProgress("uploading", persistedBytes + inFlightBytes(), partNumber);
        },
        now,
        (partSendMs) => {
          sendMs += partSendMs;
        },
      );
      inFlight.delete(partNumber);
      completed.set(partNumber, eTag);
      const parts = [...completed.entries()]
        .map(([number, tag]) => ({ partNumber: number, eTag: tag }))
        .sort((left, right) => left.partNumber - right.partNumber);
      persistedBytes = uploadedBytesForParts(completed.keys(), file.size, session.partSizeBytes);
      await store.save({
        uploadId: session.id,
        fingerprint: fileFingerprint,
        parts,
        updatedAt: Date.now(),
      });
      emitProgress("uploading", persistedBytes + inFlightBytes(), partNumber);
    };
    // One part at a time unless the caller asks for more (Xưởng ghép: 3, CEO 1a 24/10/2026).
    const queue = [...missing];
    let failed = false;
    const lane = async () => {
      while (!failed && queue.length > 0) {
        if (signal.aborted) throw abortError();
        const partNumber = queue.shift() as number;
        try {
          await uploadPart(partNumber);
        } catch (error) {
          failed = true;
          throw error;
        }
      }
    };
    const lanes = Math.max(1, Math.min(options.partConcurrency ?? 1, queue.length));
    await Promise.all(Array.from({ length: lanes }, lane));
  }

  const parts = [...completed.entries()]
    .map(([partNumber, eTag]) => ({ partNumber, eTag }))
    .sort((left, right) => left.partNumber - right.partNumber);
  emitProgress("completing", file.size);
  const version = await callWithRateLimitRetry(
    async () => {
      await options.beforeApiCall?.();
      return completeUpload(session.id, parts);
    },
    signal,
    options.onRateLimited,
  );
  await store.remove(session.id);
  emitProgress(version.status === "ready" ? "ready" : "processing", file.size);
  options.onTimed?.({ totalMs: now() - startedAt, sendMs });
  return version;
}

export async function resumeFile(
  sessionId: string,
  file: File,
  signal: AbortSignal,
  options: UploadOptions = {},
): Promise<GalleryAssetVersion> {
  const session = await getUploadSession(sessionId);
  return uploadFile(session, file, signal, options);
}

// MỘT cửa xếp hàng cho cả thẻ trình duyệt: hộp tải chia lô 100 tệp gọi
// uploadFiles nhiều lần, và máy chủ đếm trần theo NGƯỜI chứ không theo lô.
let sharedLimiter: (() => Promise<void>) | null = null;
function sharedCallLimiter(): () => Promise<void> {
  sharedLimiter ??= createCallLimiter(UPLOAD_API_CALLS_PER_MINUTE);
  return sharedLimiter;
}

export async function uploadFiles(
  sessions: GalleryUploadSession[],
  files: File[],
  signal: AbortSignal,
  options: UploadOptions = {},
): Promise<GalleryAssetVersion[]> {
  if (sessions.length !== files.length) throw new Error("Số tệp không khớp số lượt tải");
  const results = new Array<GalleryAssetVersion>(sessions.length);
  // Tối đa UPLOAD_CONCURRENCY tệp cùng lúc, nhịp gọi máy chủ đi qua MỘT cửa xếp
  // hàng chung. Trước 13/10/2026 tải từng tệp một vì bốn luồng không giới hạn đã
  // vượt trần 300 lượt/phút và đỏ cả lô 429.
  const beforeApiCall = options.beforeApiCall ?? sharedCallLimiter();
  const workers = Math.max(1, Math.min(options.concurrency ?? UPLOAD_CONCURRENCY, sessions.length));
  let next = 0;
  let failure: unknown = null;
  const worker = async () => {
    while (failure === null && next < sessions.length) {
      const index = next;
      next += 1;
      const session = sessions[index];
      const file = files[index];
      if (!session || !file) return;
      try {
        results[index] = await uploadFile(session, file, signal, {
          ...options,
          beforeApiCall,
          onProgress: (progress) => options.onProgress?.({ ...progress, fileIndex: index }),
        });
      } catch (cause) {
        // Tệp đầu tiên hỏng thì thôi nhận tệp mới; tệp đang chạy dở chạy nốt
        // (đã gửi phần nào là giữ phần đó để "Thử lại" đi tiếp).
        if (failure === null) failure = cause;
      }
    }
  };
  await Promise.all(Array.from({ length: workers }, () => worker()));
  if (failure !== null) throw failure;
  return results;
}
